import { ScreenName, OwnerSquad } from '@/analytics/generated/segment'
import { EditOrderChanges } from '@/components/business/EditOrderModal/types'
import { Categories } from '@/proxy/inventory/hooks/useAllCategories/types'
import { ActiveOrder } from '@/resources/types'
import { OrderDetails } from '@/resources/types/orderDetails'

export const processItemsListForAnalytics = (
  order?: Partial<ActiveOrder | OrderDetails>
) => {
  if (!order || !order.items) return null

  return order.items.map(
    ({ variantId, name, amount, unitPrice, totalPrice }) => ({
      product_id: variantId,
      name,
      quantity: amount,
      price: totalPrice,
      base_price: unitPrice,
    })
  )
}

export const getSelectedCategoryNames = (
  selectedCategories: string[],
  categories: Categories[]
) => {
  const result: string[] = []
  selectedCategories.forEach(categoryId => {
    const category = categories.find(cat => cat.id === categoryId)
    if (category) {
      result.push(category.name)
    }
  })
  return result
}

export const formatEditedItems = (changes: EditOrderChanges) => {
  if (changes.deletions.length > 0 || changes.replacements.length > 0) {
    const editedItems = [
      ...changes.deletions.map(deletion => ({
        original_product_name: deletion.product.name,
        original_quantity: deletion.product.amount,
        is_deleted: true,
        substitute_product_name: null,
        substitute_quantity: null,
      })),
      ...changes.replacements.map(replacement => ({
        original_product_name: replacement.original.name,
        original_quantity: replacement.original.amount,
        is_deleted: false,
        substitute_product_name: replacement.replacement.name,
        substitute_quantity: replacement.replacementQuantity,
      })),
    ]

    return editedItems
  }
  return null
}

export const pathNameToSegmentScreenName = (
  pathName: string | null
): ScreenName => {
  switch (pathName) {
    case '/login':
      return ScreenName.LoginPage
    case '/recover':
      return ScreenName.AccountRecoveryPage
    case '/cadastro-parceiro':
      return ScreenName.SignUpLandingPage
    case '/cadastro-parceiro/formulario':
      return ScreenName.SignUpForm
    case '/cadastro-parceiro/sucesso':
      return ScreenName.SignUpSuccess
    case '/poc-orders':
      return ScreenName.Orders
    case '/history':
      return ScreenName.OrderHistory
    case '/searched-products':
      return ScreenName.SearchResult
    case '/fleet-management':
      return ScreenName.FleetManagement
    case '/fleet-management/my-fleet':
      return ScreenName.MyFleet
    case '/fleet-management/no-deliveryman-linked':
      return ScreenName.NoDeliverymanLinked
    case '/my-account':
      return ScreenName.MyAccount
    case '/my-account/deliveryman-approval':
      return ScreenName.DeliverymanApproval
    case '/my-account/deliveryman-manager':
      return ScreenName.DeliverymanManager
    case '/my-account/partner-bank-details':
      return ScreenName.PartnerBankDetails
    case '/my-account/edit-poc-info':
      return ScreenName.EditPocInfo
    case '/performance':
      return ScreenName.Performance
    case '/performance/ten-minutes-delivery-release-rate':
      return ScreenName.DeliveryReleaseRate
    case '/management':
      return ScreenName.Management
    case '/dates-availability':
      return ScreenName.DatesAvailability
    case '/orders-map':
      return ScreenName.OrdersMap
    case '/on-sale':
      return ScreenName.OnSale
    case '/online-payment':
      return ScreenName.OnlinePayment
    case '/maintenance':
      return ScreenName.Maintenance
    case '/dynamic-radius':
      return ScreenName.DynamicRadius
    case '/financial-report':
      return ScreenName.FinancialReport
    case '/':
    case '/home':
      return ScreenName.Homepage
    case '/poc-orders-scheduled':
      return ScreenName.PocOrdersScheduled
    case '/inventory':
      return ScreenName.Inventory
    default: {
      const validateOrderDetail = /^\/order\/(\d+)$/
      if (pathName && validateOrderDetail.test(pathName)) {
        return ScreenName.OrderDetail
      }
      return ScreenName.NotFound
    }
  }
}

export type ActionAnalyticsParams = {
  type: 'Menu' | 'Header' | 'Home' // extend this as needed
  componentName: string
  componentCategory?: string
  ownerSquad?: OwnerSquad.DtcZeSetup | string
  macroJourney?: string | null
  microJourney?: string | null
  mediaSource?: string
  pathname: string
}

/**
 *
 * Generates analytics event name and data for user actions.
 *
 * @param {ActionAnalyticsParams} params - Parameters for creating analytics data.
 * @returns {{ eventName: string; data: Record<string, any> }} - The event name and associated data.
 */
export const createActionAnalytics = ({
  type,
  componentName,
  componentCategory,
  ownerSquad = OwnerSquad.DtcZeSetup,
  macroJourney = null,
  microJourney = null,
  mediaSource = 'ORGANIC',
  pathname,
}: ActionAnalyticsParams) => {
  const defaultCategory =
    componentCategory ?? (type === 'Menu' ? 'nav bar' : 'button')

  const eventName = `${type} Action Clicked`

  const data = {
    component_category: defaultCategory,
    component_name: componentName,
    screen_name: pathNameToSegmentScreenName(pathname),
    owner_squad: ownerSquad,
    macro_journey: macroJourney,
    micro_journey: microJourney,
    media_source: mediaSource,
  }

  return { eventName, data }
}
